// src/components/alerts/AlertsBanner.tsx
//
// Shared in-app alert banner includes weekly fixed-payment and savings review tasks.
//
// Single-row banner at the top of /home. Tapping anywhere opens the
// AlertsDrawer with every unresolved alert. Self-hides when there are
// zero active alerts so first-time / clean workspaces don't see chrome.
//
// 0.3.1 Preline pivot:
//   - 0.3.0's single "12 件事要看一下" count + left-sidebar ribbon → replaced
//     with severity-bucketed Badge pills (e.g. "2 critical · 3 warn · 5 info")
//     so the user can see severity ratio at a glance without opening the
//     drawer. Each severity gets its own soft-tinted Badge variant.
//   - Drawer rows: emoji TYPE_ICON → lucide line icons; no more font-display
//     italic titles; row bg uses card + standard 1px border (no sidebar).

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  CalendarClock,
  ChevronRight,
  Clock,
  Coins,
  Copy,
  CreditCard,
  PartyPopper,
  PiggyBank,
  Receipt,
  Target,
  TrendingDown,
  TrendingUp,
  Tv,
  X,
  type LucideProps,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { alertDestination } from '@/lib/appNavigation';
import { useWorkspaceStore } from '@/stores/workspace';
import { AppDrawer } from '@/components/ui/AppDrawer';
import { Button } from '@/components/ui/Button';
import { Badge, type BadgeVariant } from '@/components/ui/Badge';
import { toast } from '@/lib/toast';
import type { ComponentType } from 'react';

type AlertSeverity = 'info' | 'warn' | 'critical' | 'celebrate';
type AlertType =
  | 'category_spike'
  | 'goal_behind'
  | 'credit_high'
  | 'sub_unused'
  | 'pending_overdue'
  | 'income_missed'
  | 'savings_drop'
  | 'fi_milestone'
  | 'duplicate_receipt'
  | 'big_ticket'
  | 'subscription_creep'
  | 'pay_yourself_paycheck'
  | 'fixed_due_today'
  | 'savings_due_weekly';

interface AlertRow {
  id: string;
  alert_type: AlertType;
  severity: AlertSeverity;
  title: string;
  detail: string;
  data: Record<string, unknown>;
  action_url: string | null;
  generated_at: number;
}

interface AlertsResponse {
  today: string;
  alerts: AlertRow[];
  counts: {
    total: number;
    critical: number;
    warn: number;
    info: number;
    celebrate: number;
  };
}

// Lucide line-art icons replace the 0.3.0 emoji TYPE_ICON map. Picked to
// stay legible at 16px and read as the alert's domain (money, time, trend).
const TYPE_ICON: Record<AlertType, ComponentType<LucideProps>> = {
  category_spike: TrendingUp,
  goal_behind: Target,
  credit_high: CreditCard,
  sub_unused: Tv,
  pending_overdue: Clock,
  income_missed: Coins,
  savings_drop: TrendingDown,
  fi_milestone: PartyPopper,
  duplicate_receipt: Copy,
  big_ticket: Receipt,
  subscription_creep: TrendingUp,
  pay_yourself_paycheck: PiggyBank,
  fixed_due_today: CalendarClock,
  savings_due_weekly: PiggyBank,
};

// `celebrate` (backend term for positive milestones) maps to the success
// badge palette; everything else aligns 1:1 with the Badge variant names.
const SEVERITY_TO_VARIANT: Record<AlertSeverity, BadgeVariant> = {
  critical: 'critical',
  warn: 'warn',
  info: 'info',
  celebrate: 'success',
};

export function AlertsBanner() {
  const { t } = useTranslation();
  const ws = useWorkspaceStore((s) => s.current);
  const [open, setOpen] = useState(false);

  const alertsQuery = useQuery({
    queryKey: ['alerts', ws?.id],
    queryFn: () => api.get<AlertsResponse>('/alerts'),
    enabled: !!ws,
    staleTime: 60 * 1000,
  });

  const data = alertsQuery.data;
  if (!data || data.counts.total === 0) return null;

  // Severity buckets — only render the buckets that have items. Order is
  // critical → warn → info → celebrate so the most urgent reads first.
  const allBuckets: Array<{ severity: AlertSeverity; count: number; label: string }> = [
    { severity: 'critical', count: data.counts.critical, label: t('alerts.shortCritical') },
    { severity: 'warn', count: data.counts.warn, label: t('alerts.shortWarn') },
    { severity: 'info', count: data.counts.info, label: t('alerts.shortInfo') },
    { severity: 'celebrate', count: data.counts.celebrate, label: t('alerts.shortCelebrate') },
  ];
  const buckets = allBuckets.filter((b) => b.count > 0);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-2 rounded-xl border bg-card px-4 py-3 text-left shadow-card transition hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <span className="text-sm font-medium text-foreground">
          {t('alerts.bannerTitle', { count: data.counts.total })}
        </span>
        <div className="flex flex-wrap items-center gap-1.5">
          {buckets.map((b) => (
            <Badge
              key={b.severity}
              variant={SEVERITY_TO_VARIANT[b.severity]}
              size="sm"
              leadingDot
            >
              {b.count} {b.label}
            </Badge>
          ))}
        </div>
        <ChevronRight className="ml-auto h-4 w-4 text-ink-500" aria-hidden />
      </button>

      <AlertsDrawer open={open} onOpenChange={setOpen} alerts={data.alerts} />
    </>
  );
}

function AlertsDrawer(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  alerts: AlertRow[];
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ws = useWorkspaceStore((s) => s.current);
  const qc = useQueryClient();

  // Optimistic removal so taps on ✓ / × visually clear the row instantly
  // instead of waiting ~500ms+ for the server round-trip + refetch — the
  // delay had users assuming the action silently failed and tapping again.
  // On error we restore the previous snapshot from the mutation context.
  const ALERTS_KEY = ['alerts', ws?.id] as const;
  async function optimisticallyRemoveAlert(id: string): Promise<AlertsResponse | undefined> {
    await qc.cancelQueries({ queryKey: ALERTS_KEY });
    const previous = qc.getQueryData<AlertsResponse>(ALERTS_KEY);
    if (!previous) return undefined;
    const removed = previous.alerts.find((a) => a.id === id);
    if (!removed) return previous;
    const nextCounts = { ...previous.counts };
    nextCounts.total = Math.max(0, nextCounts.total - 1);
    nextCounts[removed.severity] = Math.max(0, nextCounts[removed.severity] - 1);
    qc.setQueryData<AlertsResponse>(ALERTS_KEY, {
      ...previous,
      alerts: previous.alerts.filter((a) => a.id !== id),
      counts: nextCounts,
    });
    return previous;
  }

  const dismissMutation = useMutation<unknown, Error, string, { previous?: AlertsResponse }>({
    mutationFn: (id: string) => api.post(`/alerts/${id}/dismiss`),
    onMutate: async (id) => ({ previous: await optimisticallyRemoveAlert(id) }),
    onError: (err, _id, ctx) => {
      if (ctx?.previous) qc.setQueryData(ALERTS_KEY, ctx.previous);
      toast.error(err instanceof ApiError ? err.message : t('alerts.actionFailed'));
    },
    // Reconcile with server in case of drift (parallel resolution from
    // another tab, cron auto-resolve fired between request + response, …).
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ALERTS_KEY });
    },
  });

  const resolveMutation = useMutation<unknown, Error, string, { previous?: AlertsResponse }>({
    mutationFn: (id: string) => api.post(`/alerts/${id}/resolve`),
    onMutate: async (id) => ({ previous: await optimisticallyRemoveAlert(id) }),
    onSuccess: () => {
      toast.success(t('alerts.markedResolved'));
    },
    onError: (err, _id, ctx) => {
      if (ctx?.previous) qc.setQueryData(ALERTS_KEY, ctx.previous);
      toast.error(err instanceof ApiError ? err.message : t('alerts.actionFailed'));
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ALERTS_KEY });
    },
  });

  const refreshMutation = useMutation({
    mutationFn: () => api.post('/alerts/refresh'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alerts', ws?.id] });
      toast.success(t('alerts.refreshed'));
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : t('alerts.actionFailed')),
  });

  function handleAction(alert: AlertRow) {
    props.onOpenChange(false);
    const destination = alertDestination(alert.alert_type, alert.action_url);
    if (destination) {
      navigate(destination);
    }
  }

  return (
    <AppDrawer
      open={props.open}
      onOpenChange={props.onOpenChange}
      title={t('alerts.drawerTitle')}
      description={t('alerts.drawerDesc')}
      footer={
        <Button
          variant="secondary"
          size="md"
          loading={refreshMutation.isPending}
          onClick={() => refreshMutation.mutate()}
          className="w-full"
        >
          {t('alerts.rescan')}
        </Button>
      }
    >
      <ul className="space-y-2">
        {props.alerts.map((a) => {
          const Icon = TYPE_ICON[a.alert_type];
          const variant = SEVERITY_TO_VARIANT[a.severity];
          return (
            <li
              key={a.id}
              className="rounded-xl border bg-card p-3 shadow-card"
            >
              <div className="mb-2 flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-start gap-2.5">
                  <span
                    aria-hidden
                    className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-muted text-ink-500"
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <Badge variant={variant} size="sm">
                        {a.severity}
                      </Badge>
                    </div>
                    <div className="mt-1 text-sm font-medium text-foreground line-clamp-2">
                      {a.title}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {a.detail}
                    </div>
                  </div>
                </div>
                <Button
                  variant="icon"
                  // Per-row disable: shared mutation isPending would grey
                  // out every dismiss button while one network request is
                  // in flight. variables holds the id of the row currently
                  // mutating, so other rows stay clickable.
                  disabled={
                    dismissMutation.isPending &&
                    dismissMutation.variables === a.id
                  }
                  onClick={() => dismissMutation.mutate(a.id)}
                  aria-label={t('alerts.dismiss')}
                  className="h-7 w-7"
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </Button>
              </div>
              {/* Mark-resolved row — `secondary` variant (1px border on
                  white) preserves the unambiguous-action treatment that
                  fixed 0.2.24's misread-button bug. action_url launches
                  the alert's deep-link route. */}
              <div className="mt-2 flex items-center justify-between gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={
                    resolveMutation.isPending &&
                    resolveMutation.variables === a.id
                  }
                  onClick={() => resolveMutation.mutate(a.id)}
                >
                  {t('alerts.markResolved')}
                </Button>
                {a.action_url && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleAction(a)}
                  >
                    {t(`alerts.action.${a.alert_type}`, t('alerts.actionDefault'))} →
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </AppDrawer>
  );
}
